import {
  Button,
  ConfirmDialog,
  EmptyState,
  LoadingState,
} from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectAllOutlets } from '@store/outlets';
import { ReceiptText } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  closeAccountingPeriod,
  fetchAccountingPeriods,
  reopenAccountingPeriod,
  saveAccountingPeriod,
} from '../../../../services/accountingPeriodService';
import { fetchAccountOptions } from '../../../../services/accountingService';
import {
  deleteFinancialPostingRule,
  fetchFinancialPostingRules,
  saveFinancialPostingRule,
} from '../../../../services/financialPostingRuleService';
import { fetchPaymentMethods } from '../../../../services/paymentMethodService';
import {
  deleteTaxRate,
  fetchTaxRates,
  saveTaxRate,
} from '../../../../services/taxRateService';
import {
  deletePaymentMethodAccountMapping,
  fetchPaymentMethodAccountMappings,
  savePaymentMethodAccountMapping,
} from '../../../../services/paymentMethodAccountMappingService';
import {
  AccountRecord,
  AccountingPeriodRecord,
  FinancialPostingRuleRecord,
  PaymentMethodAccountMappingRecord,
  PaymentMethodRecord,
  SaveAccountingPeriodRequest,
  SaveFinancialPostingRuleRequest,
  SavePaymentMethodAccountMappingRequest,
  SaveTaxRateRequest,
  TaxRateRecord,
} from '../../../../types/accounting';
import { apiError, apiSuccess } from '../../../../util/notify';
import { FinancialPostingRuleFormModal } from './FinancialPostingRuleFormModal';
import { FinancialPostingRulesTable } from './FinancialPostingRulesTable';
import { AccountingPeriodFormModal } from './AccountingPeriodFormModal';
import {
  AccountingPeriodFormState,
  createAccountingPeriodFormFromRecord,
  createEmptyAccountingPeriodForm,
} from './accountingPeriods';
import { AccountingPeriodsTable } from './AccountingPeriodsTable';
import { TaxRateFormModal } from './TaxRateFormModal';
import {
  createEmptyTaxRateForm,
  createTaxRateFormFromRecord,
  TaxRateFormState,
} from './taxRates';
import { TaxRatesTable } from './TaxRatesTable';
import {
  createEmptyPostingRuleForm,
  createPostingRuleFormFromRecord,
  PostingRuleFormState,
  resolvePostingRuleEventType,
  resolvePostingRuleReferenceType,
  serializePostingRuleMetadata,
} from './postingRules';
import { RefundRuleScenarioCard } from './RefundRuleScenarioCard';
import {
  createPostingRuleFormFromRefundTemplate,
  getRefundTemplateRuleCount,
  isRefundPostingRule,
  REFUND_POSTING_RULE_TEMPLATES,
  RefundPostingRuleTemplate,
} from './refundPostingRules';
import { PaymentMethodMappingsTable } from './PaymentMethodMappingsTable';
import { PaymentMethodMappingFormModal } from './PaymentMethodMappingFormModal';
import { AccountingPageInfoButton } from '../AccountingHelp';

export const FinancialPostingRulesSettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = currentUser?.outlet?.id ?? null;

  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRateRecord[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRecord[]>(
    [],
  );
  const [rules, setRules] = useState<FinancialPostingRuleRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] =
    useState<FinancialPostingRuleRecord | null>(null);
  const [ruleToDelete, setRuleToDelete] =
    useState<FinancialPostingRuleRecord | null>(null);

  const loadPostingRules = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setRules([]);
        setAccounts([]);
        setTaxRates([]);
        setPaymentMethods([]);
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const [
        accountsResponse,
        taxRatesResponse,
        paymentMethodsResponse,
        rulesResponse,
      ] = await Promise.all([
        fetchAccountOptions(companyId),
        fetchTaxRates(companyId),
        fetchPaymentMethods(),
        fetchFinancialPostingRules(companyId),
      ]);

      if (accountsResponse.success && accountsResponse.data) {
        setAccounts(accountsResponse.data);
      }

      if (taxRatesResponse.success && taxRatesResponse.data) {
        setTaxRates(taxRatesResponse.data);
      }

      if (paymentMethodsResponse.success && paymentMethodsResponse.data) {
        setPaymentMethods(paymentMethodsResponse.data);
      }

      if (!rulesResponse.success || !rulesResponse.data) {
        setError(rulesResponse.error || 'Failed to load posting rules');
        setRules([]);
      } else {
        setRules(rulesResponse.data);
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadPostingRules();
  }, [loadPostingRules]);

  const handleCreate = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rule: FinancialPostingRuleRecord) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleSave = async (form: PostingRuleFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }

    const resolvedEventType = resolvePostingRuleEventType(form);
    const resolvedReferenceType = resolvePostingRuleReferenceType(form);

    if (!resolvedEventType) {
      apiError('Select an event type or enter a custom one.');
      return;
    }

    if (
      form.referenceType &&
      !resolvedReferenceType &&
      form.referenceType.trim() !== ''
    ) {
      apiError('Select a reference type or enter a custom one.');
      return;
    }

    let metadata: Record<string, unknown> | null = null;

    try {
      metadata = serializePostingRuleMetadata(form);
    } catch {
      apiError('Advanced metadata must be valid JSON.');
      return;
    }

    const payload: SaveFinancialPostingRuleRequest = {
      id: editingRule?.id,
      companyId,
      outletId: outletId || null,
      sourceModule: form.sourceModule,
      eventType: resolvedEventType,
      referenceType: resolvedReferenceType,
      ruleName: form.ruleName,
      direction: form.direction,
      accountId: form.accountId,
      taxRateId: form.taxRateId || null,
      paymentMethodId: form.paymentMethodId
        ? Number(form.paymentMethodId)
        : null,
      priority: Number(form.priority || 100),
      status: form.status,
      metadata,
    };

    setIsSaving(true);
    const response = await saveFinancialPostingRule(payload);
    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save posting rule');
      return;
    }

    apiSuccess(editingRule ? 'Posting rule updated.' : 'Posting rule created.');
    setIsModalOpen(false);
    setEditingRule(null);
    await loadPostingRules(true);
  };

  const handleDelete = async () => {
    if (!companyId || !ruleToDelete) {
      return;
    }

    setIsDeleting(true);
    const response = await deleteFinancialPostingRule(
      companyId,
      ruleToDelete.id,
    );
    setIsDeleting(false);

    if (!response.success) {
      apiError(response.error || 'Failed to delete posting rule');
      return;
    }

    apiSuccess('Posting rule deleted.');
    setRuleToDelete(null);
    await loadPostingRules(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading posting rules..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Financial Posting Rules
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Configure source event to account mappings for automated journal
              posting.
            </p>
          </div>
          <div className="flex gap-3">
            <AccountingPageInfoButton page="posting-rules" />
            <Button
              variant="outline"
              onClick={() => loadPostingRules(true)}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
            <Button onClick={handleCreate}>Create rule</Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Rules</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {rules.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Active rules</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {rules.filter((rule) => rule.status === 'active').length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Modules covered</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {new Set(rules.map((rule) => rule.source_module)).size}
          </p>
        </div>
      </div>

      {!rules.length ? (
        <EmptyState
          title="No posting rules found"
          description="Create the first posting rule to start automating accounting events into journal entries."
          primaryAction={{ label: 'Create rule', onClick: handleCreate }}
        />
      ) : (
        <FinancialPostingRulesTable
          rules={rules}
          accounts={accounts}
          taxRates={taxRates}
          paymentMethods={paymentMethods}
          onEdit={handleEdit}
          onDelete={setRuleToDelete}
        />
      )}

      {isModalOpen ? (
        <FinancialPostingRuleFormModal
          title={editingRule ? 'Edit posting rule' : 'Create posting rule'}
          initial={
            editingRule
              ? createPostingRuleFormFromRecord(editingRule)
              : createEmptyPostingRuleForm()
          }
          accounts={accounts}
          taxRates={taxRates}
          paymentMethods={paymentMethods}
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingRule(null);
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(ruleToDelete)}
        onClose={() => setRuleToDelete(null)}
        onConfirm={handleDelete}
        title="Delete posting rule"
        message={
          ruleToDelete ? `Delete posting rule ${ruleToDelete.rule_name}?` : ''
        }
        isLoading={isDeleting}
        confirmText="Delete rule"
        tone="danger"
      />
    </div>
  );
};

export const AccountingPeriodsSettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const userId = currentUser?.id ?? null;
  const outlets = useAppSelector(selectAllOutlets);

  const [periods, setPeriods] = useState<AccountingPeriodRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] =
    useState<AccountingPeriodRecord | null>(null);
  const [periodToClose, setPeriodToClose] =
    useState<AccountingPeriodRecord | null>(null);
  const [periodToReopen, setPeriodToReopen] =
    useState<AccountingPeriodRecord | null>(null);
  const [coverageGapPrompt, setCoverageGapPrompt] = useState<{
    gapStartDate: string;
    outletId: string | null;
  } | null>(null);
  const [createPrefill, setCreatePrefill] = useState<Partial<
    ReturnType<typeof createEmptyAccountingPeriodForm>
  > | null>(null);

  const loadPeriods = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setPeriods([]);
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const response = await fetchAccountingPeriods(companyId);

      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load accounting periods');
        setPeriods([]);
      } else {
        setPeriods(response.data);
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const handleCreate = () => {
    setEditingPeriod(null);
    setCreatePrefill(null);
    setIsModalOpen(true);
  };

  const handleEdit = (period: AccountingPeriodRecord) => {
    setEditingPeriod(period);
    setIsModalOpen(true);
  };

  const handleSave = async (form: AccountingPeriodFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }

    const payload: SaveAccountingPeriodRequest = {
      id: editingPeriod?.id,
      companyId,
      outletId: form.outletId,
      name: form.name,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      notes: form.notes || null,
    };

    setIsSaving(true);
    const response = await saveAccountingPeriod(payload);
    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save accounting period');
      return;
    }

    apiSuccess(
      editingPeriod
        ? 'Accounting period updated.'
        : 'Accounting period created.',
    );
    setIsModalOpen(false);
    setEditingPeriod(null);
    await loadPeriods(true);
  };

  const handleClosePeriod = async () => {
    if (!periodToClose) {
      return;
    }

    setIsClosing(true);
    const response = await closeAccountingPeriod(periodToClose.id, userId);
    setIsClosing(false);

    if (!response.success) {
      apiError(response.error || 'Failed to close accounting period');
      return;
    }

    apiSuccess('Accounting period closed.');

    if (response.data?.leavesCoverageGap && response.data.gapStartDate) {
      setCoverageGapPrompt({
        gapStartDate: response.data.gapStartDate,
        outletId: periodToClose.outlet_id,
      });
    }

    setPeriodToClose(null);
    await loadPeriods(true);
  };

  const handleReopenPeriod = async () => {
    if (!periodToReopen) {
      return;
    }

    setIsReopening(true);
    const response = await reopenAccountingPeriod(periodToReopen.id);
    setIsReopening(false);

    if (!response.success) {
      apiError(response.error || 'Failed to reopen accounting period');
      return;
    }

    apiSuccess('Accounting period reopened.');
    setPeriodToReopen(null);
    await loadPeriods(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading accounting periods..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Accounting Periods
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Open periods accept postings, while closed periods lock the
              covered dates.
            </p>
          </div>
          <div className="flex gap-3">
            <AccountingPageInfoButton page="accounting-periods" />
            <Button
              variant="outline"
              onClick={() => loadPeriods(true)}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
            <Button onClick={handleCreate}>Create period</Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Periods</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {periods.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Open periods</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {periods.filter((period) => period.status === 'open').length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Closed periods</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {periods.filter((period) => period.status === 'closed').length}
          </p>
        </div>
      </div>

      {!periods.length ? (
        <EmptyState
          title="No accounting periods found"
          description="Create the first accounting period before posting journals into the ledger."
          primaryAction={{ label: 'Create period', onClick: handleCreate }}
        />
      ) : (
        <AccountingPeriodsTable
          periods={periods}
          outlets={outlets}
          onEdit={handleEdit}
          onClosePeriod={setPeriodToClose}
          onReopenPeriod={setPeriodToReopen}
        />
      )}

      {coverageGapPrompt ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              No open accounting period covers dates from{' '}
              {coverageGapPrompt.gapStartDate} onward for this scope. Create the
              next period now to avoid blocking settlements and checkouts.
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCoverageGapPrompt(null)}
              >
                Dismiss
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditingPeriod(null);
                  setCreatePrefill({
                    outletId: coverageGapPrompt.outletId,
                    periodStart: coverageGapPrompt.gapStartDate,
                  });
                  setIsModalOpen(true);
                  setCoverageGapPrompt(null);
                }}
              >
                Create next period
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isModalOpen ? (
        <AccountingPeriodFormModal
          title={
            editingPeriod
              ? 'Edit accounting period'
              : 'Create accounting period'
          }
          initial={
            editingPeriod
              ? createAccountingPeriodFormFromRecord(editingPeriod)
              : createEmptyAccountingPeriodForm(createPrefill || undefined)
          }
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingPeriod(null);
            setCreatePrefill(null);
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(periodToClose)}
        onClose={() => setPeriodToClose(null)}
        onConfirm={handleClosePeriod}
        title="Close accounting period"
        message={
          periodToClose
            ? `Close ${periodToClose.name}? Journals dated within this period will no longer be postable.`
            : ''
        }
        isLoading={isClosing}
        confirmText="Close period"
        tone="danger"
      />

      <ConfirmDialog
        isOpen={Boolean(periodToReopen)}
        onClose={() => setPeriodToReopen(null)}
        onConfirm={handleReopenPeriod}
        title="Reopen accounting period"
        message={
          periodToReopen
            ? `Reopen ${periodToReopen.name}? It will accept postings again until closed.`
            : ''
        }
        isLoading={isReopening}
        confirmText="Reopen period"
      />
    </div>
  );
};

export const TaxRatesSettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRateRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRateRecord | null>(
    null,
  );
  const [taxRateToDelete, setTaxRateToDelete] = useState<TaxRateRecord | null>(
    null,
  );

  const loadTaxRates = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setTaxRates([]);
        setAccounts([]);
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const [accountsResponse, taxRatesResponse] = await Promise.all([
        fetchAccountOptions(companyId),
        fetchTaxRates(companyId),
      ]);

      if (accountsResponse.success && accountsResponse.data) {
        setAccounts(accountsResponse.data);
      }

      if (!taxRatesResponse.success || !taxRatesResponse.data) {
        setError(taxRatesResponse.error || 'Failed to load tax rates');
        setTaxRates([]);
      } else {
        setTaxRates(taxRatesResponse.data);
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadTaxRates();
  }, [loadTaxRates]);

  const handleCreate = () => {
    setEditingTaxRate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (taxRate: TaxRateRecord) => {
    setEditingTaxRate(taxRate);
    setIsModalOpen(true);
  };

  const handleSave = async (form: TaxRateFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }

    const payload: SaveTaxRateRequest = {
      id: editingTaxRate?.id,
      companyId,
      code: form.code,
      name: form.name,
      rate: Number(form.rate || 0),
      application: form.application,
      status: form.status,
      outputAccountId: form.outputAccountId || null,
      inputAccountId: form.inputAccountId || null,
      description: form.description || null,
    };

    setIsSaving(true);
    const response = await saveTaxRate(payload);
    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save tax rate');
      return;
    }

    apiSuccess(editingTaxRate ? 'Tax rate updated.' : 'Tax rate created.');
    setIsModalOpen(false);
    setEditingTaxRate(null);
    await loadTaxRates(true);
  };

  const handleDelete = async () => {
    if (!companyId || !taxRateToDelete) {
      return;
    }

    setIsDeleting(true);
    const response = await deleteTaxRate(companyId, taxRateToDelete.id);
    setIsDeleting(false);

    if (!response.success) {
      apiError(response.error || 'Failed to delete tax rate');
      return;
    }

    apiSuccess('Tax rate deleted.');
    setTaxRateToDelete(null);
    await loadTaxRates(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading tax rates..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">Tax Rates</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage tax codes, applications, and input/output account mappings.
            </p>
          </div>
          <div className="flex gap-3">
            <AccountingPageInfoButton page="tax-rates" />
            <Button
              variant="outline"
              onClick={() => loadTaxRates(true)}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
            <Button onClick={handleCreate}>Create tax rate</Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {!taxRates.length ? (
        <EmptyState
          title="No tax rates found"
          description="Create your first tax rate to support accounting and automated tax posting."
          primaryAction={{ label: 'Create tax rate', onClick: handleCreate }}
        />
      ) : (
        <TaxRatesTable
          taxRates={taxRates}
          accounts={accounts}
          onEdit={handleEdit}
          onDelete={setTaxRateToDelete}
        />
      )}

      {isModalOpen ? (
        <TaxRateFormModal
          title={editingTaxRate ? 'Edit tax rate' : 'Create tax rate'}
          initial={
            editingTaxRate
              ? createTaxRateFormFromRecord(editingTaxRate)
              : createEmptyTaxRateForm()
          }
          accounts={accounts}
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTaxRate(null);
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(taxRateToDelete)}
        onClose={() => setTaxRateToDelete(null)}
        onConfirm={handleDelete}
        title="Delete tax rate"
        message={
          taxRateToDelete
            ? `Delete tax rate ${taxRateToDelete.code} - ${taxRateToDelete.name}?`
            : ''
        }
        isLoading={isDeleting}
        confirmText="Delete tax rate"
        tone="danger"
      />
    </div>
  );
};

export const PaymentMappingsSettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRecord[]>(
    [],
  );
  const [mappings, setMappings] = useState<PaymentMethodAccountMappingRecord[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethodRecord | null>(null);
  const [editingMapping, setEditingMapping] =
    useState<PaymentMethodAccountMappingRecord | null>(null);
  const [mappingToDelete, setMappingToDelete] =
    useState<PaymentMethodAccountMappingRecord | null>(null);

  const loadMappings = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setAccounts([]);
        setMappings([]);
        setPaymentMethods([]);
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const [accountsResponse, methodsResponse, mappingsResponse] =
        await Promise.all([
          fetchAccountOptions(companyId),
          fetchPaymentMethods(),
          fetchPaymentMethodAccountMappings(companyId),
        ]);

      if (accountsResponse.success && accountsResponse.data) {
        setAccounts(accountsResponse.data);
      }

      if (methodsResponse.success && methodsResponse.data) {
        setPaymentMethods(methodsResponse.data);
      }

      if (!mappingsResponse.success || !mappingsResponse.data) {
        setError(mappingsResponse.error || 'Failed to load payment mappings');
        setMappings([]);
      } else {
        setMappings(mappingsResponse.data);
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const handleOpenEditor = (
    mapping: PaymentMethodAccountMappingRecord | null,
    method: PaymentMethodRecord,
  ) => {
    setEditingMapping(mapping);
    setSelectedMethod(method);
  };

  const handleSave = async (accountId: string) => {
    if (!companyId || !selectedMethod) {
      return;
    }

    const payload: SavePaymentMethodAccountMappingRequest = {
      id: editingMapping?.id,
      companyId,
      paymentMethodId: selectedMethod.id,
      accountId,
    };

    setIsSaving(true);
    const response = await savePaymentMethodAccountMapping(payload);
    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save payment mapping');
      return;
    }

    apiSuccess(
      editingMapping ? 'Payment mapping updated.' : 'Payment mapping created.',
    );
    setSelectedMethod(null);
    setEditingMapping(null);
    await loadMappings(true);
  };

  const handleDelete = async () => {
    if (!companyId || !mappingToDelete) {
      return;
    }

    setIsDeleting(true);
    const response = await deletePaymentMethodAccountMapping(
      companyId,
      mappingToDelete.id,
    );
    setIsDeleting(false);

    if (!response.success) {
      apiError(response.error || 'Failed to delete payment mapping');
      return;
    }

    apiSuccess('Payment mapping removed.');
    setMappingToDelete(null);
    await loadMappings(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading payment mappings..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-black">
              Payment Method Mappings
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Define which balance-sheet or receivable account each settlement
              method should post into.
            </p>
          </div>
          <div className="flex gap-3">
            <AccountingPageInfoButton page="payment-mappings" />
            <Button
              variant="outline"
              onClick={() => loadMappings(true)}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Methods</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {paymentMethods.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Mapped</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {mappings.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Unmapped</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {Math.max(0, paymentMethods.length - mappings.length)}
          </p>
        </div>
      </div>

      {!paymentMethods.length ? (
        <EmptyState
          title="No payment methods found"
          description="Payment methods need to exist before you can configure accounting mappings."
        />
      ) : (
        <PaymentMethodMappingsTable
          paymentMethods={paymentMethods}
          mappings={mappings}
          accounts={accounts}
          onEdit={handleOpenEditor}
          onDelete={setMappingToDelete}
        />
      )}

      {selectedMethod ? (
        <PaymentMethodMappingFormModal
          accounts={accounts}
          isSaving={isSaving}
          mapping={editingMapping}
          paymentMethod={selectedMethod}
          onClose={() => {
            setSelectedMethod(null);
            setEditingMapping(null);
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(mappingToDelete)}
        onClose={() => setMappingToDelete(null)}
        onConfirm={handleDelete}
        title="Remove payment mapping"
        message="Remove this payment method mapping? The payment method will remain available operationally, but accounting postings will no longer resolve through this mapping."
        isLoading={isDeleting}
        confirmText="Remove mapping"
        tone="danger"
      />
    </div>
  );
};

export const TaxRefundsSettingsPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = currentUser?.outlet?.id ?? null;

  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRateRecord[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRecord[]>(
    [],
  );
  const [rules, setRules] = useState<FinancialPostingRuleRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialForm, setInitialForm] = useState<PostingRuleFormState>(
    createEmptyPostingRuleForm(),
  );
  const [editingRule, setEditingRule] =
    useState<FinancialPostingRuleRecord | null>(null);
  const [ruleToDelete, setRuleToDelete] =
    useState<FinancialPostingRuleRecord | null>(null);

  const loadRefundRules = useCallback(
    async (isRefresh = false) => {
      if (!companyId) {
        setError('Company details are missing for this account.');
        setRules([]);
        setAccounts([]);
        setTaxRates([]);
        setPaymentMethods([]);
        setIsLoading(false);
        return;
      }

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const [
        accountsResponse,
        taxRatesResponse,
        paymentMethodsResponse,
        rulesResponse,
      ] = await Promise.all([
        fetchAccountOptions(companyId),
        fetchTaxRates(companyId),
        fetchPaymentMethods(),
        fetchFinancialPostingRules(companyId),
      ]);

      if (accountsResponse.success && accountsResponse.data) {
        setAccounts(accountsResponse.data);
      }

      if (taxRatesResponse.success && taxRatesResponse.data) {
        setTaxRates(taxRatesResponse.data);
      }

      if (paymentMethodsResponse.success && paymentMethodsResponse.data) {
        setPaymentMethods(paymentMethodsResponse.data);
      }

      if (!rulesResponse.success || !rulesResponse.data) {
        setError(rulesResponse.error || 'Failed to load tax refund rules');
        setRules([]);
      } else {
        setRules(rulesResponse.data.filter(isRefundPostingRule));
        setError(null);
      }

      setIsLoading(false);
      setIsRefreshing(false);
    },
    [companyId],
  );

  useEffect(() => {
    loadRefundRules();
  }, [loadRefundRules]);

  const openCreateModal = (template?: RefundPostingRuleTemplate) => {
    setEditingRule(null);
    setInitialForm(
      template
        ? createPostingRuleFormFromRefundTemplate(template)
        : createEmptyPostingRuleForm(),
    );
    setIsModalOpen(true);
  };

  const handleEdit = (rule: FinancialPostingRuleRecord) => {
    setEditingRule(rule);
    setInitialForm(createPostingRuleFormFromRecord(rule));
    setIsModalOpen(true);
  };

  const handleSave = async (form: PostingRuleFormState) => {
    if (!companyId) {
      apiError('Company details are missing for this account.');
      return;
    }

    const resolvedEventType = resolvePostingRuleEventType(form);
    const resolvedReferenceType = resolvePostingRuleReferenceType(form);

    if (!resolvedEventType) {
      apiError('Select an event type or enter a custom one.');
      return;
    }

    if (
      form.referenceType &&
      !resolvedReferenceType &&
      form.referenceType.trim() !== ''
    ) {
      apiError('Select a reference type or enter a custom one.');
      return;
    }

    let metadata: Record<string, unknown> | null = null;

    try {
      metadata = serializePostingRuleMetadata(form);
    } catch {
      apiError('Advanced metadata must be valid JSON.');
      return;
    }

    const payload: SaveFinancialPostingRuleRequest = {
      id: editingRule?.id,
      companyId,
      outletId: outletId || null,
      sourceModule: form.sourceModule,
      eventType: resolvedEventType,
      referenceType: resolvedReferenceType,
      ruleName: form.ruleName,
      direction: form.direction,
      accountId: form.accountId,
      taxRateId: form.taxRateId || null,
      paymentMethodId: form.paymentMethodId
        ? Number(form.paymentMethodId)
        : null,
      priority: Number(form.priority || 100),
      status: form.status,
      metadata,
    };

    setIsSaving(true);
    const response = await saveFinancialPostingRule(payload);
    setIsSaving(false);

    if (!response.success) {
      apiError(response.error || 'Failed to save tax refund rule');
      return;
    }

    apiSuccess(editingRule ? 'Refund rule updated.' : 'Refund rule created.');
    setIsModalOpen(false);
    setEditingRule(null);
    await loadRefundRules(true);
  };

  const handleDelete = async () => {
    if (!companyId || !ruleToDelete) {
      return;
    }

    setIsDeleting(true);
    const response = await deleteFinancialPostingRule(
      companyId,
      ruleToDelete.id,
    );
    setIsDeleting(false);

    if (!response.success) {
      apiError(response.error || 'Failed to delete refund rule');
      return;
    }

    apiSuccess('Refund rule deleted.');
    setRuleToDelete(null);
    await loadRefundRules(true);
  };

  if (isLoading) {
    return <LoadingState message="Loading tax refund rules..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-pos-primary/10 text-pos-primary">
              <ReceiptText size={22} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-black">Tax Refunds</h2>
              <p className="mt-1 text-sm text-gray-600">
                Configure refund-focused posting rules for outlet sales,
                cancelled paid bookings, retained cancellation fees, and OTA
                refund adjustments.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <AccountingPageInfoButton page="tax-refunds" />
            <Button
              variant="outline"
              onClick={() => loadRefundRules(true)}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
            <Button onClick={() => openCreateModal()}>
              Create refund rule
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Refund rules</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {rules.length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-3.5">
          <p className="text-sm font-medium text-gray-600">Active rules</p>
          <p className="mt-2 text-2xl font-semibold text-black">
            {rules.filter((rule) => rule.status === 'active').length}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-600">Scenarios covered</p>
          <p className="mt-3 text-3xl font-semibold text-black">
            {
              REFUND_POSTING_RULE_TEMPLATES.filter(
                (template) => getRefundTemplateRuleCount(rules, template) > 0,
              ).length
            }
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {REFUND_POSTING_RULE_TEMPLATES.map((template) => (
          <RefundRuleScenarioCard
            key={template.id}
            template={template}
            configuredRules={getRefundTemplateRuleCount(rules, template)}
            onCreateRule={openCreateModal}
          />
        ))}
      </div>

      {!rules.length ? (
        <EmptyState
          title="No refund rules found"
          description="Start by adding refund posting rules for the scenarios in your PRD so future refund event capture can post consistently."
          primaryAction={{
            label: 'Create refund rule',
            onClick: () => openCreateModal(REFUND_POSTING_RULE_TEMPLATES[0]),
          }}
        />
      ) : (
        <FinancialPostingRulesTable
          rules={rules}
          accounts={accounts}
          taxRates={taxRates}
          paymentMethods={paymentMethods}
          onEdit={handleEdit}
          onDelete={setRuleToDelete}
        />
      )}

      {isModalOpen ? (
        <FinancialPostingRuleFormModal
          title={editingRule ? 'Edit refund rule' : 'Create refund rule'}
          initial={initialForm}
          accounts={accounts}
          taxRates={taxRates}
          paymentMethods={paymentMethods}
          isSaving={isSaving}
          onClose={() => {
            setIsModalOpen(false);
            setEditingRule(null);
            setInitialForm(createEmptyPostingRuleForm());
          }}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(ruleToDelete)}
        onClose={() => setRuleToDelete(null)}
        onConfirm={handleDelete}
        title="Delete refund rule"
        message={
          ruleToDelete ? `Delete refund rule ${ruleToDelete.rule_name}?` : ''
        }
        isLoading={isDeleting}
        confirmText="Delete rule"
        tone="danger"
      />
    </div>
  );
};
